import React, { useCallback, useMemo } from "react";
import { SimpleTable } from "../../UniversalCRMTable/SimpleTable";
import type {
  BaseEntity,
  SimpleTableConfig,
  TableFilters,
  PaginationState,
  EntityHooks,
  SerializedViewState,
} from "../../UniversalCRMTable/types";
import type {
  ConfigWithEnhancedBulkActions,
  assertEnhancedBulkActions,
} from "@/lib/types/bulk-actions-enhanced";

interface UseUniversalEntityPageCallbacksProps<T extends BaseEntity> {
  entityName: string;
  /**
   * CRITICAL: This config MUST have enhanced bulk actions with onClick handlers
   * This is enforced by the type system to prevent the ordering bug
   * where callbacks receive config without handlers
   */
  config: ConfigWithEnhancedBulkActions<T>;
  // Data
  data: any;
  isLoading: boolean;
  processedData: T[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  tableFilters: TableFilters;
  setTableFilters: (filters: TableFilters) => void;
  currentPage: number;
  pagination: PaginationState;
  // Handlers
  handleEditItem: (item: T) => void;
  handleViewItem: (item: T) => void;
  handleInlineEdit: (item: T) => Promise<void>;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (size: number) => void;
  handleExportWithScope: (scope: string, format: string) => Promise<void>;
  handleSavedViewsReady: (
    serialize: () => SerializedViewState,
    apply: (filters: SerializedViewState, isManualSelection?: boolean) => void
  ) => void;
  handleOpenDetailModal: (item: T, ids: string[], index: number) => void;
  // Filter state
  serializeFilters: (() => SerializedViewState) | null;
  applyFilters: ((filters: SerializedViewState, isManualSelection?: boolean) => void) | null;
  // Hooks
  hooks: EntityHooks<T>;
  groupableFields: any;
  // Infinite scroll
  loadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  infiniteScrollEnabled?: boolean;
  /** When true, the scroll event listener auto-triggers loadMore. When false, only the button triggers it. */
  autoScrollEnabled?: boolean;
}

/**
 * Memoized callbacks and render functions for UniversalEntityPage
 * Isolates performance-critical memoization logic
 *
 * CRITICAL: This hook MUST receive config with enhanced bulk actions
 * The config must have onClick handlers on all bulk actions
 * This is enforced by the type system and runtime assertions
 */
export function useUniversalEntityPageCallbacks<T extends BaseEntity>({
  entityName,
  config,
  data,
  isLoading,
  processedData,
  searchTerm,
  setSearchTerm,
  tableFilters,
  setTableFilters,
  currentPage,
  pagination,
  handleEditItem,
  handleViewItem,
  handleInlineEdit,
  handlePageChange,
  handlePageSizeChange,
  handleExportWithScope,
  handleSavedViewsReady,
  handleOpenDetailModal,
  serializeFilters,
  applyFilters,
  hooks,
  groupableFields,
  loadMore,
  hasNextPage,
  isFetchingNextPage,
  infiniteScrollEnabled,
  autoScrollEnabled,
}: UseUniversalEntityPageCallbacksProps<T>) {
  // Runtime assertion to catch type violations
  // This will throw if config doesn't have enhanced bulk actions
  if (config.bulkActions && config.bulkActions.length > 0) {
    const missingHandlers = config.bulkActions.filter((action) => !("onClick" in action));
    if (missingHandlers.length > 0) {
      throw new Error(
        `Config passed to useUniversalEntityPageCallbacks has bulk actions without onClick handlers: ${missingHandlers.map((a) => a.id).join(", ")}. ` +
          "This is a hook ordering issue in useUniversalEntityPageComposition. " +
          "Ensure bulk actions are enhanced BEFORE callbacks are created."
      );
    }
  }
  // ============================================================================
  // PERFORMANCE OPTIMIZED CALLBACKS FOR GROUPING WRAPPER
  // ============================================================================

  // Memoized export handler for GroupingWrapper
  const handleGroupingExport = useCallback(
    (data: T[], format: string) => {
      const exportFormat = format === "csv" ? "csv" : format === "json" ? "csv" : "csv";
      void handleExportWithScope("current-page", exportFormat);
    },
    [handleExportWithScope]
  );

  // Memoized render function for SimpleTable to prevent unnecessary re-renders
  const renderSimpleTable = useCallback(
    (params?: Record<string, unknown>) => {
      const {
        processedData: groupedProcessedData = [],
        groupingContext,
        groupableFields: contextGroupableFields,
        isCardView = false,
        hideTableBody = false,
      } = (params || {}) as { processedData?: T[]; groupingContext?: any; groupableFields?: any; isCardView?: boolean; hideTableBody?: boolean };
      return (
        <SimpleTable
          config={config}
          data={groupedProcessedData}
          isLoading={isLoading}
          error={null}
          onEdit={handleEditItem}
          onView={handleViewItem}
          onInlineEdit={handleInlineEdit ? (payload) => handleInlineEdit(payload) : undefined}
          onSavedViewsReady={handleSavedViewsReady}
          externalFilters={tableFilters}
          onFiltersChange={setTableFilters}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isDataPreFiltered={true}
          // Pagination (cursor-based)
          currentPage={currentPage}
          totalPages={
            data?.totalCount
              ? Math.max(1, Math.ceil((data.totalCount || 0) / (pagination.pageSize || 1)))
              : undefined
          }
          pageSize={pagination.pageSize}
          totalItems={data?.totalCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          hooks={hooks}
          groupingContext={groupingContext}
          groupableFields={contextGroupableFields}
          onOpenDetailModal={handleOpenDetailModal}
          hideColumnHeaders={isCardView}
          hideTableBody={hideTableBody}
          // Load More + scroll-to-fetch only in infinite-scroll mode; pagination mode uses TablePagination
          loadMore={infiniteScrollEnabled ? loadMore : undefined}
          hasNextPage={infiniteScrollEnabled ? hasNextPage : undefined}
          isFetchingNextPage={infiniteScrollEnabled ? isFetchingNextPage : undefined}
          autoScrollEnabled={infiniteScrollEnabled ? (autoScrollEnabled ?? false) : false}
        />
      );
    },
    [
      config,
      isLoading,
      handleEditItem,
      handleViewItem,
      handleInlineEdit,
      handleSavedViewsReady,
      tableFilters,
      setTableFilters,
      searchTerm,
      setSearchTerm,
      currentPage,
      data?.totalCount,
      pagination.pageSize,
      handlePageChange,
      handlePageSizeChange,
      hooks,
      handleOpenDetailModal,
      loadMore,
      hasNextPage,
      isFetchingNextPage,
      autoScrollEnabled,
      infiniteScrollEnabled,
    ]
  );

  return {
    handleGroupingExport,
    renderSimpleTable,
  };
}
